import Place from "./Place";

export default class Trip {
  public from!: Place;
  public to!: Place;

  public constructor(init?: Partial<Trip>) {
    Object.assign(this, init);
    if (init) {
      if (init.from) {
        this.from = new Place(init.from);
      }
      if (init.to) {
        this.to = new Place(init.to);
      }
    }
  }

  /**
   * BIK-2148: are these the same postal code, ignoring the ZIP+4 suffix?
   *
   * The two sides of a trip are built by different producers: a Google Places pick keeps
   * the 5-digit ZIP it returns ("78628"), while a pasted address carries whatever AVS
   * answered, which is the +4 form ("78628-8707"). Comparing the raw strings therefore
   * failed for one and the same address, and the same-location warning never fired when
   * one side was pasted and the other came from a saved address.
   *
   * Only the base is compared: the +4 narrows a ZIP down to a block face, so two
   * addresses that agree on the first five digits are in the same postal area either way
   * — which is the granularity this check has always used.
   */
  private static samePostalCode(a?: string, b?: string): boolean {
    const base = (value?: string): string =>
      String(value ?? "").trim().split("-")[0].trim().toUpperCase();
    const left = base(a);
    const right = base(b);
    // Two blanks are not a match: an empty component means "unknown", not "equal".
    if (!left || !right) return false;
    return left === right;
  }

  public isSameLocation(): boolean {
    try {
      if (this.from && this.to) {
        if (
          this.from.formatted_address === this.to.formatted_address ||
          (this.from.originalFormattedAddress &&
            this.to.originalFormattedAddress &&
            this.from.originalFormattedAddress ===
              this.to.originalFormattedAddress)
        ) {
          return true;
        }
        // components breakdown check by country, state and postal code
        const fromComponents = {} as any;
        if (
          this.from.address_components &&
          this.from.address_components.constructor === Array
        ) {
          for (let i = 0; i < this.from.address_components.length; i++) {
            const component = this.from.address_components[i];
            const type = component.types[0];
            switch (type) {
              case "postal_code":
                fromComponents.postalCode = component.long_name;
                break;
              case "country":
                fromComponents.countryCode = component.short_name;
                break;
              case "administrative_area_level_1":
                fromComponents.state = component.short_name;
                break;
              default:
                break;
            }
          }
        }
        const toComponents = {} as any;
        if (
          this.to.address_components &&
          this.to.address_components.constructor == Array
        ) {
          for (let i = 0; i < this.to.address_components.length; i++) {
            const component = this.to.address_components[i];
            const type = component.types[0];
            switch (type) {
              case "postal_code":
                toComponents.postalCode = component.long_name;
                break;
              case "country":
                toComponents.countryCode = component.short_name;
                break;
              case "administrative_area_level_1":
                toComponents.state = component.short_name;
                break;
              default:
                break;
            }
          }
        }
        if (
          Trip.samePostalCode(
            fromComponents.postalCode,
            toComponents.postalCode
          ) &&
          fromComponents.countryCode === toComponents.countryCode &&
          fromComponents.state == toComponents.state
        ) {
          return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  public updateFormattedAddressLabels() {
    try {
      if (this.from) this.from.formatLabel(this.from, false);
      if (this.to) this.to.formatLabel(this.to, false);
    } catch (e) {}
  }
}
